import { createEmptyDocument, normalizeLine } from '../models'
import type {
  BudgetCommand,
  BudgetDocument,
  BudgetEvent,
  BudgetValidationResult,
} from '../types'
import { createBudgetEvent } from '../events'
import { budgetCalculator } from '../core/BudgetCalculator'
import { budgetValidator } from '../validators'
import { createId, generateBudgetNumber } from '../utils'

export interface CommandContext {
  nowIso: string
  idFactory: () => string
}

export interface CommandApplyResult {
  document: BudgetDocument
  events: BudgetEvent[]
  validation?: BudgetValidationResult
  recordHistory: boolean
}

export function applyCommand(
  document: BudgetDocument | null,
  command: BudgetCommand,
  ctx: CommandContext,
): CommandApplyResult {
  switch (command.type) {
    case 'CreateBudget':
      return applyCreate(command, ctx)
    case 'RestoreSnapshot':
      return applyRestore(command, ctx)
    default:
      if (!document) {
        throw Object.assign(new Error('BUDGET_NOT_INITIALIZED'), {
          code: 'BUDGET_NOT_INITIALIZED',
        })
      }
      return applyOnDocument(document, command, ctx)
  }
}

function applyCreate(
  command: Extract<BudgetCommand, { type: 'CreateBudget' }>,
  ctx: CommandContext,
): CommandApplyResult {
  const id = ctx.idFactory()
  const doc = createEmptyDocument({
    id,
    number: generateBudgetNumber(new Date(ctx.nowIso)),
    title: command.title?.trim() || 'Novo Orçamento',
    client: command.client,
    nowIso: ctx.nowIso,
  })
  const document = stamp(budgetCalculator.recompute(doc), ctx.nowIso)
  return {
    document,
    recordHistory: false,
    events: [
      createBudgetEvent({
        type: 'budget.created',
        budgetId: document.id,
        commandId: command.commandId,
        nowIso: ctx.nowIso,
        payload: { number: document.number, title: document.title },
      }),
      createBudgetEvent({
        type: 'budget.recomputed',
        budgetId: document.id,
        commandId: command.commandId,
        nowIso: ctx.nowIso,
        payload: { totals: document.totals },
      }),
    ],
  }
}

function applyRestore(
  command: Extract<BudgetCommand, { type: 'RestoreSnapshot' }>,
  ctx: CommandContext,
): CommandApplyResult {
  const document = stamp(
    budgetCalculator.recompute(command.snapshot.document),
    ctx.nowIso,
    command.snapshot.document.version + 1,
  )
  return {
    document,
    recordHistory: false,
    events: [
      createBudgetEvent({
        type: 'budget.restored',
        budgetId: document.id,
        commandId: command.commandId,
        nowIso: ctx.nowIso,
        payload: { version: document.version },
      }),
    ],
  }
}

function applyOnDocument(
  current: BudgetDocument,
  command: BudgetCommand,
  ctx: CommandContext,
): CommandApplyResult {
  const events: BudgetEvent[] = []
  let next = current
  let validation: BudgetValidationResult | undefined
  let recordHistory = true

  switch (command.type) {
    case 'SetTitle':
      next = { ...current, title: command.title.trim() }
      events.push(
        createBudgetEvent({
          type: 'budget.updated',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { field: 'title', title: next.title },
        }),
      )
      break

    case 'SetClient':
      next = { ...current, client: command.client }
      events.push(
        createBudgetEvent({
          type: 'budget.client_set',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { client: command.client },
        }),
      )
      break

    case 'AddLine': {
      const id = command.line.id ?? ctx.idFactory()
      const position = command.line.position ?? current.lines.length
      const line = normalizeLine({
        ...command.line,
        id,
        position,
        origin: command.line.origin,
      })
      next = { ...current, lines: [...current.lines, line] }
      events.push(
        createBudgetEvent({
          type: 'budget.line_added',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { lineId: line.id },
        }),
      )
      break
    }

    case 'UpdateLine': {
      const lines = current.lines.map((line) => {
        if (line.id !== command.lineId) return line
        return normalizeLine({
          ...line,
          ...command.patch,
          id: line.id,
          position: command.patch.position ?? line.position,
          origin: command.patch.origin ?? line.origin,
        })
      })
      if (!current.lines.some((line) => line.id === command.lineId)) {
        throw Object.assign(new Error('LINE_NOT_FOUND'), { code: 'LINE_NOT_FOUND' })
      }
      next = { ...current, lines }
      events.push(
        createBudgetEvent({
          type: 'budget.line_updated',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { lineId: command.lineId },
        }),
      )
      break
    }

    case 'RemoveLine': {
      if (!current.lines.some((line) => line.id === command.lineId)) {
        throw Object.assign(new Error('LINE_NOT_FOUND'), { code: 'LINE_NOT_FOUND' })
      }
      next = {
        ...current,
        lines: current.lines.filter((line) => line.id !== command.lineId),
      }
      events.push(
        createBudgetEvent({
          type: 'budget.line_removed',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { lineId: command.lineId },
        }),
      )
      break
    }

    case 'ReorderLines': {
      const map = new Map(current.lines.map((line) => [line.id, line]))
      if (command.lineIds.length !== current.lines.length) {
        throw Object.assign(new Error('REORDER_LENGTH_MISMATCH'), {
          code: 'REORDER_LENGTH_MISMATCH',
        })
      }
      const lines = command.lineIds.map((id, position) => {
        const line = map.get(id)
        if (!line) {
          throw Object.assign(new Error('LINE_NOT_FOUND'), { code: 'LINE_NOT_FOUND' })
        }
        return { ...line, position }
      })
      next = { ...current, lines }
      events.push(
        createBudgetEvent({
          type: 'budget.lines_reordered',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { lineIds: command.lineIds },
        }),
      )
      break
    }

    case 'SetGlobalDiscount':
      next = {
        ...current,
        globalDiscountPercent: command.discountPercent ?? current.globalDiscountPercent,
        globalDiscountValue: command.discountValue ?? current.globalDiscountValue,
      }
      events.push(
        createBudgetEvent({
          type: 'budget.discount_set',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: {
            discountPercent: next.globalDiscountPercent,
            discountValue: next.globalDiscountValue,
          },
        }),
      )
      break

    case 'SetShipping':
      next = { ...current, shipping: Math.max(0, command.shipping) }
      events.push(
        createBudgetEvent({
          type: 'budget.shipping_set',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { shipping: next.shipping },
        }),
      )
      break

    case 'SetTaxRate':
      next = { ...current, taxRatePercent: Math.max(0, command.taxRatePercent) }
      events.push(
        createBudgetEvent({
          type: 'budget.updated',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { field: 'taxRatePercent', taxRatePercent: next.taxRatePercent },
        }),
      )
      break

    case 'SetStatus':
      next = { ...current, status: command.status }
      events.push(
        createBudgetEvent({
          type: 'budget.status_changed',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { from: current.status, to: command.status },
        }),
      )
      break

    case 'SetNotes':
      next = { ...current, notes: command.notes }
      events.push(
        createBudgetEvent({
          type: 'budget.updated',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { field: 'notes' },
        }),
      )
      break

    case 'Recompute':
      recordHistory = false
      events.push(
        createBudgetEvent({
          type: 'budget.recomputed',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: {},
        }),
      )
      break

    case 'Validate': {
      recordHistory = false
      validation = budgetValidator.validate(current)
      events.push(
        createBudgetEvent({
          type: 'budget.validated',
          budgetId: current.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: validation,
        }),
      )
      next = current
      break
    }

    default:
      throw Object.assign(new Error('UNKNOWN_COMMAND'), { code: 'UNKNOWN_COMMAND' })
  }

  if (command.type !== 'Validate') {
    next = stamp(budgetCalculator.recompute(next), ctx.nowIso, current.version + 1)
    if (command.type !== 'Recompute') {
      events.push(
        createBudgetEvent({
          type: 'budget.recomputed',
          budgetId: next.id,
          commandId: command.commandId,
          nowIso: ctx.nowIso,
          payload: { totals: next.totals },
        }),
      )
    }
  }

  return { document: next, events, validation, recordHistory }
}

function stamp(document: BudgetDocument, nowIso: string, version?: number): BudgetDocument {
  return {
    ...document,
    version: version ?? document.version,
    updatedAt: nowIso,
  }
}

export function createCommandId(): string {
  return createId()
}
